import { useState, useEffect } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Plus, Edit, Trash2, Check, X, Save, Loader2, GripVertical, Star, Tag, Filter } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import AdminTagGroups from "./AdminTagGroups";

const TABS = [
  { id: "districts", labelKey: "admin_taxonomy:tab_cities", singularKey: "admin_taxonomy:singular_city", endpoint: "districts", idField: "id" },
  { id: "areas", labelKey: "admin_taxonomy:tab_areas", singularKey: "admin_taxonomy:singular_area", endpoint: "areas", idField: "area_id" },
  { id: "venue-types", labelKey: "admin_taxonomy:tab_venue_types", singularKey: "admin_taxonomy:singular_venue_type", endpoint: "venue-types", idField: "id" },
  { id: "event-types", labelKey: "admin_taxonomy:tab_event_types", singularKey: "admin_taxonomy:singular_event_type", endpoint: "event-types", idField: "id" },
  // Tag Groups — managed inline via the embedded <AdminTagGroups /> page when
  // this tab is active. Lives next to Tags so admins can create a group and
  // populate it with tags without leaving Taxonomy.
  { id: "tag-groups", labelKey: "admin_taxonomy:tab_tag_groups", singularKey: "admin_taxonomy:singular_tag_group", endpoint: "tag-groups", idField: "group_id", embed: "tag-groups" },
  // Tags are the unified taxonomy axis — cuisines + dietary tags + the
  // legacy "tag categories" tab were retired. Every chip type now lives
  // here, scoped via Tag Groups (managed in the previous tab).
  { id: "tags", labelKey: "admin_taxonomy:tab_tags", singularKey: "admin_taxonomy:singular_tag", endpoint: "tags", idField: "tag_id" },
];

const idOf = (item, activeTab) => {
  if (activeTab === "tags") return item.tag_id;
  if (activeTab === "areas") return item.area_id;
  return item.id;
};

// Grid column template per tab — keeps header and rows aligned.
const columnTemplate = (activeTab) => {
  // handle | name | name_el | slug | [category|district] | active | [featured] | actions
  if (activeTab === "tags") return "28px 1.2fr 1.2fr 1.2fr 1fr 80px 140px";
  if (activeTab === "areas") return "28px 1.2fr 1.2fr 1.2fr 1fr 80px 80px 120px";
  return "28px 1.5fr 1.5fr 1.5fr 80px 120px";
};

export default function AdminTaxonomy() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("districts");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Tags-tab filter: "" = all groups, "__none__" = ungrouped, otherwise a tag_group_slug
  const [tagGroupFilter, setTagGroupFilter] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [districtsRes, typesRes, etypesRes, tagsRes, areasRes, tagGroupsRes] = await Promise.all([
        axios.get(`${API}/taxonomy/districts`),
        axios.get(`${API}/taxonomy/venue-types`),
        axios.get(`${API}/taxonomy/event-types`),
        axios.get(`${API}/taxonomy/tags?public_only=false`),
        axios.get(`${API}/taxonomy/areas`),
        axios.get(`${API}/admin/taxonomy/tag-groups`),
      ]);
      setData({
        districts: districtsRes.data,
        "venue-types": typesRes.data,
        "event-types": etypesRes.data,
        tags: tagsRes.data,
        areas: areasRes.data,
        "tag-categories": tagGroupsRes.data || [],
      });
    } catch (error) {
      console.error("Error fetching taxonomy:", error);
    } finally {
      setLoading(false);
    }
  };

  const currentTab = TABS.find((t) => t.id === activeTab);
  // For the Tags tab, apply the tagGroupFilter dropdown.
  const rawData = data[activeTab] || [];
  const currentData = activeTab === "tags" && tagGroupFilter
    ? rawData.filter((tag) => {
        if (tagGroupFilter === "__none__") return !tag.tag_group_slug;
        return tag.tag_group_slug === tagGroupFilter;
      })
    : rawData;

  const endpointFor = (tabId, id) => {
    // Tags use the admin/taxonomy prefix; areas/event-types etc. use the
    // same prefix but their endpoint segment matches the tab id.
    if (tabId === "tags") return id ? `${API}/admin/taxonomy/tags/${id}` : `${API}/admin/taxonomy/tags`;
    return id ? `${API}/admin/taxonomy/${tabId}/${id}` : `${API}/admin/taxonomy/${tabId}`;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const item = deleteTarget;
    const id = idOf(item, activeTab);
    setDeleting(true);
    try {
      await axios.delete(endpointFor(activeTab, id));
      setData({ ...data, [activeTab]: currentData.filter((i) => idOf(i, activeTab) !== id) });
      toast.success(t("admin_taxonomy:toast_deleted", { defaultValue: "Deleted successfully" }));
      setDeleteTarget(null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t("admin_taxonomy:toast_delete_failed", { defaultValue: "Failed to delete" }));
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdate = async (item, updates) => {
    const id = idOf(item, activeTab);
    try {
      const res = await axios.put(endpointFor(activeTab, id), updates);
      setData({
        ...data,
        [activeTab]: currentData.map((i) =>
          idOf(i, activeTab) === id ? res.data : i
        ),
      });
      setEditingItem(null);
      toast.success("Updated successfully");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleCreate = async (formData) => {
    try {
      const res = await axios.post(endpointFor(currentTab.endpoint, null), formData);
      setData({ ...data, [activeTab]: [...currentData, res.data] });
      setShowCreate(false);
      toast.success("Created successfully");
    } catch (error) {
      throw error;
    }
  };

  const handleToggleFeatured = async (item) => {
    await handleUpdate(item, { is_featured: !item.is_featured });
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(currentData);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    // optimistic update
    setData({ ...data, [activeTab]: reordered });
    const ids = reordered.map((i) => idOf(i, activeTab));
    try {
      const url = `${API}/admin/taxonomy/${activeTab}/reorder`;
      await axios.post(url, { ids });
      toast.success("Order saved");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to save order");
      fetchAll();
    }
  };

  const gridCols = columnTemplate(activeTab);
  // When the embedded sub-page handles its own chrome (e.g., its own Add
  // button + table), we just slot it under the tab strip and skip the
  // generic Taxonomy CRUD UI.
  const isEmbedded = !!currentTab?.embed;

  return (
    <div data-testid="admin-taxonomy-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><Tag className="h-6 w-6 text-primary" />{t("admin_taxonomy:title")}</h1>
        {!isEmbedded && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="taxonomy-create-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t("admin_taxonomy:add_prefix", { defaultValue: "Add" })} {currentTab ? t(currentTab.singularKey) : ""}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("admin_taxonomy:create_title", { defaultValue: "Create" })} {currentTab ? t(currentTab.singularKey) : ""}</DialogTitle>
              </DialogHeader>
              <CreateForm
                activeTab={activeTab}
                districts={data.districts || []}
                venueTypes={data["venue-types"] || []}
                tagCategories={data["tag-categories"] || []}
                onSubmit={handleCreate}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((tab) => {
          // Tag Groups tab pulls its count from the cached tag-categories
          // payload (the same backend collection); other tabs use their
          // own data array.
          const countKey = tab.embed === "tag-groups" ? "tag-categories" : tab.id;
          return (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              data-testid={`taxonomy-tab-${tab.id}`}
            >
              {tab.label ? tab.label : t(tab.labelKey)} ({(data[countKey] || []).length})
            </Button>
          );
        })}
      </div>

      {isEmbedded ? (
        <AdminTagGroups />
      ) : (
      <>
      <p className="text-xs text-muted-foreground mb-3">
        {t("admin_taxonomy:drag_hint", { defaultValue: "Drag the handle to reorder items. Lower rows sort first." })}
        {activeTab === "areas" && " " + t("admin_taxonomy:areas_star_hint", { defaultValue: "Click the star to feature an area in the homepage chip row." })}
      </p>

      {activeTab === "tags" && (
        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="tags-filter-bar">
          <Label className="text-xs text-muted-foreground">
            {t("admin_taxonomy:filter_by_group", { defaultValue: "Filter by Tag Group:" })}
          </Label>
          <Select value={tagGroupFilter || "__all__"} onValueChange={(v) => setTagGroupFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-[260px] text-sm" data-testid="tags-filter-group">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("admin_taxonomy:filter_all", { defaultValue: "All groups" })}</SelectItem>
              <SelectItem value="__none__">{t("admin_taxonomy:filter_ungrouped", { defaultValue: "— Ungrouped (no group) —" })}</SelectItem>
              {(data["tag-categories"] || []).filter((g) => g.is_active !== false).map((g) => (
                <SelectItem key={g.group_id} value={g.slug}>{g.name}{g.name_el ? ` / ${g.name_el}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {t("admin_taxonomy:filter_count", { defaultValue: "Showing" })} {currentData.length} / {rawData.length}
          </span>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse bg-muted h-64 rounded-xl" />
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {/* Header */}
          <div
            className="grid items-center px-4 py-3 border-b border-border text-sm font-medium text-muted-foreground bg-muted/30"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div />
            <div>{t("admin_taxonomy:col_name_en", { defaultValue: "Name (EN)" })}</div>
            <div>{t("admin_taxonomy:col_name_el", { defaultValue: "Name (EL)" })}</div>
            <div>{t("admin_taxonomy:col_slug", { defaultValue: "Slug" })}</div>
            {activeTab === "tags" && <div>{t("admin_taxonomy:col_group", { defaultValue: "Group" })}</div>}
            {activeTab === "areas" && <div>{t("admin_taxonomy:col_city", { defaultValue: "District" })}</div>}
            <div>{t("admin_taxonomy:col_active", { defaultValue: "Active" })}</div>
            {activeTab === "areas" && <div>{t("admin_taxonomy:col_featured", { defaultValue: "Featured" })}</div>}
            <div className="text-right">{t("admin_venues:col_actions")}</div>
          </div>

          {currentData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {t("admin_taxonomy:no_items", { defaultValue: "No items yet. Click \"Add\" to create one." })}
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId={`tax-${activeTab}`}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {currentData.map((item, index) => {
                      const id = idOf(item, activeTab);
                      const isEditing = editingItem?.id === id;
                      if (isEditing) {
                        return (
                          <EditRow
                            key={id}
                            item={item}
                            activeTab={activeTab}
                            districts={data.districts || []}
                            gridCols={gridCols}
                            onSave={(updates) => handleUpdate(item, updates)}
                            onCancel={() => setEditingItem(null)}
                          />
                        );
                      }
                      return (
                        <Draggable key={id} draggableId={id} index={index}>
                          {(dragProvided, snapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`grid items-center px-4 py-3 border-b border-border text-sm ${
                                snapshot.isDragging ? "bg-accent shadow-lg" : "hover:bg-muted/30"
                              }`}
                              style={{
                                gridTemplateColumns: gridCols,
                                ...dragProvided.draggableProps.style,
                              }}
                              data-testid={`taxonomy-row-${id}`}
                            >
                              <div
                                {...dragProvided.dragHandleProps}
                                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                                data-testid={`taxonomy-drag-${id}`}
                              >
                                <GripVertical className="h-4 w-4" />
                              </div>
                              <div className="font-medium truncate">{item.name}</div>
                              <div className="text-muted-foreground truncate">{item.name_el || "\u2014"}</div>
                              <div className="text-muted-foreground text-xs truncate">{item.slug}</div>
                              {activeTab === "tags" && (
                                <div className="text-xs">
                                  {/* Inline category picker — writes tag_group_slug
                                      on the tag so the public site groups it
                                      under the right Tag Category. */}
                                  <Select
                                    value={item.tag_group_slug || "__none__"}
                                    onValueChange={(v) => handleUpdate(item, { tag_group_slug: v === "__none__" ? "" : v })}
                                  >
                                    <SelectTrigger className="h-7 text-xs" data-testid={`tag-cat-${id}`}><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">— None —</SelectItem>
                                      {(data["tag-categories"] || []).filter((g) => g.is_active !== false).map((g) => (
                                        <SelectItem key={g.group_id} value={g.slug}>{g.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              {activeTab === "areas" && (
                                <div className="text-muted-foreground text-xs truncate">{item.district_name || item.city_name || "\u2014"}</div>
                              )}
                              <div>
                                {item.is_active ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <X className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                              {activeTab === "areas" && (
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => handleToggleFeatured(item)}
                                    className="transition-colors"
                                    data-testid={`taxonomy-featured-${id}`}
                                    title={item.is_featured ? "Unfeature" : "Feature on homepage"}
                                  >
                                    <Star
                                      className={`h-4 w-4 ${
                                        item.is_featured
                                          ? "fill-amber-400 text-amber-400"
                                          : "text-muted-foreground hover:text-amber-400"
                                      }`}
                                    />
                                  </button>
                                </div>
                              )}
                              <div className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {activeTab === "tags" && (
                                    <AppliesToButton
                                      item={item}
                                      venueTypes={data["venue-types"] || []}
                                      onSave={(applies_to) => handleUpdate(item, { applies_to })}
                                    />
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingItem({ id, ...item })}
                                    data-testid={`taxonomy-edit-${id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteTarget(item)}
                                    data-testid={`taxonomy-delete-${id}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      )}
      </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-taxonomy-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin_taxonomy:delete_title", { defaultValue: "Delete this item?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin_taxonomy:delete_desc", { defaultValue: "Delete \"{{name}}\". This action cannot be undone.", name: deleteTarget?.name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("admin_venues:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-taxonomy">
              {t("admin_venues:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditRow({ item, activeTab, districts, gridCols, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: item.name,
    name_el: item.name_el || "",
    slug: item.slug,
    is_active: item.is_active !== false,
    district_id: item.district_id || item.city_id || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const updates = { name: form.name, name_el: form.name_el, slug: form.slug, is_active: form.is_active };
    if (activeTab === "areas") updates.district_id = form.district_id;
    await onSave(updates);
    setSaving(false);
  };

  return (
    <div
      className="grid items-center px-4 py-2 border-b border-border bg-muted/20 text-sm"
      style={{ gridTemplateColumns: gridCols }}
    >
      <div />
      <div>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 text-sm" />
      </div>
      <div>
        <Input value={form.name_el} onChange={(e) => setForm({ ...form, name_el: e.target.value })} className="h-8 text-sm" placeholder="Greek name" />
      </div>
      <div>
        <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="h-8 text-sm" />
      </div>
      {activeTab === "tags" && <div />}
      {activeTab === "areas" && (
        <div>
          <Select value={form.district_id} onValueChange={(v) => setForm({ ...form, district_id: v })}>
            <SelectTrigger className="h-8 text-sm" data-testid="taxonomy-edit-area-district">
              <SelectValue placeholder="Select district" />
            </SelectTrigger>
            <SelectContent>
              {districts.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Select
          value={form.is_active ? "active" : "inactive"}
          onValueChange={(v) => setForm({ ...form, is_active: v === "active" })}
        >
          <SelectTrigger className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {activeTab === "areas" && <div />}
      <div className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="taxonomy-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateForm({ activeTab, districts, venueTypes = [], tagCategories = [], onSubmit }) {
  const [form, setForm] = useState({
    name: "",
    name_el: "",
    slug: "",
    description: "",
    is_active: true,
    tag_group_slug: "",
    district_id: "",
    source: "tags",
    applies_to: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (activeTab === "areas" && !form.district_id) {
      setError("Please select a district for this area.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (activeTab !== "tags") { delete payload.tag_group_slug; }
      if (activeTab !== "areas") delete payload.district_id;
      // `source` is a tag_groups-only concept (managed at /admin/tag-groups)
      delete payload.source;
      // `applies_to` is currently only meaningful when creating a tag
      // (per-tag scoping); for other taxonomies just drop it.
      if (activeTab !== "tags") delete payload.applies_to;
      await onSubmit(payload);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {activeTab === "areas" && (
        <div>
          <Label>District *</Label>
          <Select value={form.district_id} onValueChange={(v) => setForm({ ...form, district_id: v })}>
            <SelectTrigger className="mt-1" data-testid="taxonomy-create-area-district">
              <SelectValue placeholder="Select district" />
            </SelectTrigger>
            <SelectContent>
              {districts.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>Name (EN) *</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value, slug: generateSlug(e.target.value) })}
          required
          className="mt-1"
          data-testid="taxonomy-create-name"
        />
      </div>

      <div>
        <Label>Name (EL)</Label>
        <Input
          value={form.name_el}
          onChange={(e) => setForm({ ...form, name_el: e.target.value })}
          className="mt-1"
          placeholder="Greek name"
          data-testid="taxonomy-create-name-el"
        />
      </div>

      <div>
        <Label>Slug *</Label>
        <Input
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })}
          required
          className="mt-1"
          data-testid="taxonomy-create-slug"
        />
      </div>

      {activeTab === "tags" && (
        <div>
          <Label>Group *</Label>
          <Select value={form.tag_group_slug || "__none__"} onValueChange={(v) => setForm({ ...form, tag_group_slug: v === "__none__" ? "" : v })}>
            <SelectTrigger className="mt-1" data-testid="taxonomy-create-tag-category">
              <SelectValue placeholder="— None —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {tagCategories.filter((g) => g.is_active !== false).map((g) => (
                <SelectItem key={g.group_id} value={g.slug}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Group this tag (e.g., Cuisine, Music, Mood, Amenity, Feature). Tags without a group won't show on the public chip rows.</p>
          <div className="mt-3">
            <Label>Applies to (venue types)</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 max-h-40 overflow-y-auto border border-border rounded p-2" data-testid="taxonomy-create-tag-applies-to">
              {venueTypes.length === 0 && <p className="text-xs text-muted-foreground">No venue types yet.</p>}
              {venueTypes.map((vt) => {
                const checked = (form.applies_to || []).includes(vt.slug);
                return (
                  <label key={vt.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = new Set(form.applies_to || []);
                        if (e.target.checked) next.add(vt.slug); else next.delete(vt.slug);
                        setForm({ ...form, applies_to: Array.from(next) });
                      }}
                    />
                    <span>{vt.name}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Leave empty to inherit the parent Tag Group's scope.</p>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        New items are added to the end of the list. Drag rows to reorder after creation.
      </p>

      <Button type="submit" disabled={loading} className="w-full" data-testid="taxonomy-create-submit">
        {loading ? "Creating..." : "Create"}
      </Button>
    </form>
  );
}

/**
 * Tiny popover-style dialog for editing the `applies_to` venue-type list of a
 * cuisine or dietary tag. Empty list = universal (shows under every venue type).
 */
function AppliesToButton({ item, venueTypes, onSave }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(Array.isArray(item.applies_to) ? item.applies_to : []);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setSelected(Array.isArray(item.applies_to) ? item.applies_to : []);
  }, [item.applies_to]);

  const toggle = (slug) => {
    setSelected((curr) => curr.includes(slug) ? curr.filter((s) => s !== slug) : [...curr, slug]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(selected);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const summary = !selected.length
    ? t("admin_taxonomy:applies_to_universal", { defaultValue: "All" })
    : selected.length === 1
      ? (venueTypes.find(vt => vt.slug === selected[0])?.name || selected[0])
      : `${selected.length}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 gap-1 text-xs"
          title={t("admin_taxonomy:applies_to_title", { defaultValue: "Edit which venue types this applies to" })}
          data-testid={`taxonomy-applies-to-${item.id}`}
        >
          <Filter className="h-3 w-3" />
          {summary}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("admin_taxonomy:applies_to_dialog_title", { defaultValue: "Applies to which venue types?" })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            <strong>{item.name}</strong>{item.name_el && ` (${item.name_el})`}.{" "}
            {t("admin_taxonomy:applies_to_help", { defaultValue: "Pick the venue types where this should appear. Leave empty to show it for all types." })}
          </p>
          <div className="flex flex-wrap gap-2">
            {venueTypes.map((vt) => {
              const active = selected.includes(vt.slug);
              return (
                <button
                  key={vt.id}
                  type="button"
                  onClick={() => toggle(vt.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 text-foreground border-border hover:bg-muted"}`}
                  data-testid={`taxonomy-applies-chip-${vt.slug}`}
                >
                  {vt.name}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>
              {t("admin_taxonomy:cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`taxonomy-applies-save-${item.id}`}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              {t("admin_taxonomy:save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

